import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  IconButton,
  Chip
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import axios from 'axios';

const ImportOrders = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showErrors, setShowErrors] = useState(true);
  const [showImported, setShowImported] = useState(true);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.endsWith('.csv')) {
        setError('Solo file CSV sono permessi');
        setFile(null);
        return;
      }
      setFile(selectedFile);
      setError(null);
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Seleziona un file CSV da caricare');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setUploading(true);
    setError(null);
    setResult(null);

    try {
      const response = await axios.post('/api/import/orders/csv', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setResult(response.data);
      setFile(null);
      // Reset file input
      document.getElementById('csv-file-input').value = '';
    } catch (err) {
      console.error('Error uploading CSV:', err);
      setError(err.response?.data?.error || err.response?.data?.details || 'Errore durante il caricamento del file');
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = 'product_code,quantity,customer_id,machine_id,commessa,reference,notes\nPROD001,100,1,1,COM-2025-001,REF001,Note esempio\n';
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'template_ordini.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Importa Ordini da CSV
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Carica un file CSV per importare ordini in blocco. Il file deve contenere le colonne:
          product_code, quantity, customer_id (opzionale), machine_id (opzionale), commessa (opzionale),
          reference (opzionale), notes (opzionale).
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            onClick={downloadTemplate}
            sx={{ mb: 2 }}
          >
            Scarica Template CSV
          </Button>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUploadIcon />}
              disabled={uploading}
            >
              Scegli File CSV
              <input
                id="csv-file-input"
                type="file"
                hidden
                accept=".csv"
                onChange={handleFileChange}
              />
            </Button>

            {file && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  {file.name} ({(file.size / 1024).toFixed(2)} KB)
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleUpload}
                  disabled={uploading}
                  startIcon={uploading ? <CircularProgress size={20} /> : null}
                >
                  {uploading ? 'Caricamento...' : 'Carica'}
                </Button>
              </Box>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {result && (
          <Box sx={{ mt: 3 }}>
            <Alert
              severity={result.errors > 0 ? 'warning' : 'success'}
              icon={result.errors > 0 ? <ErrorIcon /> : <CheckCircleIcon />}
              sx={{ mb: 2 }}
            >
              <Typography variant="body1">
                <strong>Importazione completata:</strong> {result.imported} ordini importati,{' '}
                {result.errors} errori
              </Typography>
            </Alert>

            {/* Imported Orders Section */}
            {result.details.imported.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => setShowImported(!showImported)}
                  >
                    {showImported ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                  <Typography variant="h6">
                    Ordini Importati ({result.details.imported.length})
                  </Typography>
                </Box>

                <Collapse in={showImported}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>ID</TableCell>
                          <TableCell>Prodotto</TableCell>
                          <TableCell>Quantità</TableCell>
                          <TableCell>Cliente ID</TableCell>
                          <TableCell>Macchina ID</TableCell>
                          <TableCell>Commessa</TableCell>
                          <TableCell>Stato</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.details.imported.map((order) => (
                          <TableRow key={order.id}>
                            <TableCell>{order.id}</TableCell>
                            <TableCell>{order.product_code}</TableCell>
                            <TableCell>{order.quantity}</TableCell>
                            <TableCell>{order.customer_id || '-'}</TableCell>
                            <TableCell>{order.machine_id || '-'}</TableCell>
                            <TableCell>{order.commessa || '-'}</TableCell>
                            <TableCell>
                              <Chip label={order.status} color="success" size="small" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Box>
            )}

            {/* Errors Section */}
            {result.details.errors.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => setShowErrors(!showErrors)}
                  >
                    {showErrors ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                  <Typography variant="h6" color="error">
                    Errori ({result.details.errors.length})
                  </Typography>
                </Box>

                <Collapse in={showErrors}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Riga</TableCell>
                          <TableCell>Errore</TableCell>
                          <TableCell>Dati</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result.details.errors.map((err, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{err.line}</TableCell>
                            <TableCell>
                              <Typography variant="body2" color="error">
                                {err.error}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">
                                {JSON.stringify(err.data)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Collapse>
              </Box>
            )}
          </Box>
        )}

        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="caption" component="div" gutterBottom>
            <strong>Formato CSV richiesto:</strong>
          </Typography>
          <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace' }}>
            product_code,quantity,customer_id,machine_id,commessa,reference,notes{'\n'}
            PROD001,100,1,1,COM-001,REF-001,Note esempio
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            • product_code e quantity sono obbligatori{'\n'}
            • customer_id, machine_id, commessa, reference, notes sono opzionali{'\n'}
            • Usa '-' o lascia vuoto per campi opzionali non necessari
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export default ImportOrders;
